import type { AnalysisReport } from '../types';

const CDN = 'https://d1u30siow2tw3d.cloudfront.net';

function post(tiktokId: string, tiktokUrl: string) {
  return {
    videoUrl: `${CDN}/posts/videos/tiktok/${tiktokId}_0.mp4`,
    posterUrl: `${CDN}/posts/posters/tiktok/${tiktokId}_0.jpeg`,
    sourceUrl: tiktokUrl,
  };
}

export const sunscreenCampaignAnalysis: AnalysisReport = {
  id: 'melixir-engagement-score-2026-06-16',
  title: "Melixir — 'Engagement score' (2026.06.16)",
  subtitle: '164 posts analyzed · TikTok Beauty Campaign · Melixir AirFit Invisible Sunscreen',
  platform: 'TikTok',
  metric: 'Engagement score',
  postsAnalyzed: 164,
  period: 'May 29 – June 8, 2026',
  insights: [
    {
      type: 'do',
      title: 'Use text overlay / silent video format instead of voice narration',
      detail:
        'Posts delivered through text overlays — without any voice narration — consistently drive higher engagement than voiceover formats. This holds even within the Hispanic creator group: switching from Spanish voice narration to a text overlay format produces a clear and meaningful uplift with the same audience.',
      uplift: 'Text overlay format outperforms Spanish voiceover among Hispanic creators',
      tags: ['text overlay', 'silent video', 'language format'],
      contentGuide:
        'Skip the voiceover entirely and let text overlays carry your message. Use a trending background track, write your key points as one-line captions that appear sequentially on screen, and make sure every claim reads clearly without sound — most viewers watch on mute. Think of each text line as a quick punch, not a sentence.',
    },
    {
      type: 'do',
      title: 'Feature East Asian creators delivering content in English',
      detail:
        'The East Asian creator + English language combination achieves significantly higher engagement than any other demographic pairing in this dataset — well above the overall campaign average. Against intuition, white English-speaking creators actually underperform compared to several other groups, including Hispanic Spanish-speaking content.',
      uplift: 'East Asian + English far outperforms all other demographic combinations',
      tags: ['East Asian creator', 'English content', 'creator demographics'],
      contentGuide:
        'Deliver your entire review in English in a natural, conversational tone — like recommending the product to a close friend. Film in a personal setting (car, bedroom, vanity) with good natural light, and list benefits quickly one after another rather than building slowly toward a conclusion.',
    },
    {
      type: 'do',
      title: 'Open with a hybrid hook: striking visual + instant benefit promise within 3 seconds',
      detail:
        'A hybrid hook — pairing a visually arresting moment with an immediate written benefit promise — substantially outperforms either a pure visual pattern interrupt or a conversational opener alone. The highest-performing opening combines a close-up skin application shot with a bold text overlay like "Glass skin under makeup" placed on screen in the first three seconds.',
      uplift: 'Hybrid hooks outperform pure visual or conversational openers',
      tags: ['hybrid hook', 'first 3 seconds', 'benefit promise', 'pattern interrupt'],
      contentGuide:
        'Do not open with a product shot or self-introduction. Begin with a close-up of applying sunscreen to your cheek, and place "Glass skin under makeup" — or a direct benefit promise — as text on screen within the first two seconds. The visual and the text should land simultaneously for maximum stopping power.',
    },
    {
      type: 'do',
      title: 'Script as a rapid-fire benefit list or FAB structure — not PAS',
      detail:
        'Rapid-fire benefit lists and FAB (Feature–Advantage–Benefit) structures consistently outperform the PAS (Problem–Agitation–Solution) formula, which produced the lowest average engagement in this dataset. PAS scripts that follow a rigid "old sunscreens are heavy → but this one is different" formula feel formulaic and tend to lose viewers early.',
      uplift: 'Rapid-fire lists outperform PAS — the lowest-performing script format in this dataset',
      tags: ['FAB structure', 'rapid-fire list', 'script format'],
      contentGuide:
        'List your top benefits back-to-back in under 10 seconds: "No white cast. No tackiness. Absorbs instantly. And your skin looks lit from within." Do not build tension with a problem statement first — get to the payoff fast and let each benefit land on its own before moving to the next.',
    },
    {
      type: 'do',
      title: 'Anchor your hook on "Glass skin under makeup" and close with an outdoor wear test',
      detail:
        '"Glass skin under makeup" is the single phrase shared across the top-performing posts regardless of creator demographics, format, or language — placing it in the first three seconds is the highest-leverage writing decision available. Adding a brief outdoor wear-test clip later in the video further boosts credibility by visually answering the viewer\'s core question.',
      uplift: 'The highest-performing posts share this anchor phrase regardless of format or language',
      tags: ['glass skin', 'anchor message', 'wear test', 'outdoor proof'],
      contentGuide:
        'Use "Glass skin under makeup" as your hook text in the first two seconds — or a close paraphrase of it. Then, somewhere in the final 10–15 seconds, step outside and face the camera: show that your skin still looks fresh and luminous after wearing the sunscreen for a few hours.',
    },
    {
      type: 'dont',
      title: "Don't add inclusivity messaging unless you already have a community around that skin concern",
      detail:
        "Standalone inclusivity claims — 'for all skin tones' or 'no white cast for dark skin' — do not independently drive higher engagement unless they come from a creator who already runs a dedicated series for that audience. Without an established community, the framing reads as performative and fails to deliver the trust signals the message needs to land.",
      uplift: 'Standalone inclusivity framing does not outperform standard reviews',
      tags: ['inclusivity messaging', 'creator community', 'trust signals'],
    },
    {
      type: 'dont',
      title: "Don't count on a simple face application shot as a differentiator",
      detail:
        'The vast majority of posts in this campaign already include a basic face-application scene, making it table stakes rather than a point of difference. What actually separates high performers is the quality of the close-up — capturing the precise moment of absorption or the skin\'s luminosity under well-controlled light.',
      uplift: 'Application shots are ubiquitous — close-up quality is the real differentiator',
      tags: ['application shot', 'close-up quality', 'differentiation'],
    },
  ],
  topVideoPosts: [
    {
      rank: 1,
      title: 'Silent Text-Overlay Glass Skin Demonstration',
      gmv: 'Engagement score: 1.806 (top overall)',
      creatorProfile: 'White female, age 22, US',
      description:
        'A completely silent video — no voiceover, no background music — where every message is delivered through sequential text overlays. Opens with a close-up of sunscreen being spread across the cheek in a zigzag pattern, followed by lines like "Glass skin under makeup. This is what this sunscreen actually does," "No white cast," and "No pilling." Ends by layering foundation over the sunscreen to demonstrate makeup compatibility, all under soft diffused lighting that makes the skin glow visibly.',
      reasons: [
        'Text-overlay-only format removes all language barriers',
        'Strong benefit-first hook lands in the first two seconds',
        'Close-up skin application is visually persuasive',
        'Complete narrative arc: application → foundation → final result',
      ],
      ...post('7647623630662208788', 'https://www.tiktok.com/@rbkdevelasco/video/7647623630662208788'),
    },
    {
      rank: 2,
      title: 'East Asian English Car Confessional Review',
      gmv: 'Engagement score: 1.667 (top overall)',
      creatorProfile: 'East Asian female, age 30, English, US',
      description:
        'Filmed in a parked car — a Hello Kitty cushion visible in the back seat adds personality to the setting. Natural car window light highlights the skin\'s moisture. The creator delivers a rapid-fire benefit list entirely in English: "hyaluronic acid, Korean rice extract, water-resistant, lightweight and fast-absorbing." Ends with a transparent disclosure of the gifted product, which paradoxically reinforces trust rather than undermining it.',
      reasons: [
        'East Asian + English — the highest-performing demographic combination in this dataset',
        'Car confessional format bypasses typical ad fatigue',
        'Rapid-fire benefit listing maintains viewer attention throughout',
        'Gifted product disclosure builds credibility rather than eroding it',
      ],
      ...post('7647391687903399199', 'https://www.tiktok.com/@askmims/video/7647391687903399199'),
    },
    {
      rank: 3,
      title: 'Outdoor GRWM — Hispanic Creator, Natural Setting',
      gmv: 'Engagement score: 1.246 (above average)',
      creatorProfile: 'Hispanic female, age 25, US',
      description:
        'Shot in a park or garden with trees and sunlight in the background — authentic outdoor natural light. Opens with "Come Try This With Me," immediately pulling the viewer in as a participant. The creator shares a relatable frustration — always searching for a sunscreen that feels lightweight and not sticky — before introducing the product. Demonstrates application on the face and describes the moisturizer-like texture. Emotional background music sustains a positive mood throughout.',
      reasons: [
        'Outdoor natural setting signals authenticity and real-world use',
        'Participatory "come try this with me" opening builds shared experience',
        'Relatable pain point creates genuine connection before the product reveal',
        'Hispanic creator using accessible, natural filming style without heavy editing',
      ],
      ...post('7647618084609510687', 'https://www.tiktok.com/@briannacardnas/video/7647618084609510687'),
    },
    {
      rank: 4,
      title: 'Pattern Interrupt Hook + Outdoor Wear Test',
      gmv: 'Engagement score: 1.074 (above average)',
      creatorProfile: 'Southeast Asian female, age 25, US',
      description:
        'Opens with the sunscreen tube held directly in front of the camera, partially obscuring the creator\'s face — a pure pattern interrupt. "Glass skin under makeup. This is what this sunscreen actually does" text appears immediately. A FAB-structured script follows: feature (ingredients), advantage (lightweight, fast absorbing), benefit (glass skin effect). Closes with a walk down an outdoor driveway to demonstrate the sunscreen holding up after wear — the wear-test segment that elevates credibility.',
      reasons: [
        'Visual pattern interrupt + benefit promise land simultaneously in the first two seconds',
        'Outdoor wear test segment visually proves the product holds up in real conditions',
        'Complete narrative: application → absorption close-up → outdoor result',
        'FAB structure keeps information density high without feeling slow',
      ],
      ...post('7649096596214254861', 'https://www.tiktok.com/@createwithme__07/video/7649096596214254861'),
    },
    {
      rank: 5,
      title: '"Dark Skinned Sunday" Series — Episode 4',
      gmv: 'Engagement score: 0.982 (above average)',
      creatorProfile: 'Black female, age 25, US',
      description:
        'The fourth episode in a recurring series that tests popular beauty products specifically on dark skin tones — purple/pink neon LED lights set the distinctive visual identity of the series. The creator openly tests for white cast and shininess on deep skin, confirms "no white cast, no shine," and honestly flags one downside (slight tackiness) — a detail that dramatically increases viewer trust. Closes with "Will you try it?" to drive comments and a teaser for the next episode to prompt follows.',
      reasons: [
        'Pre-existing series format brings an established and loyal fan base to every video',
        'Honest disclosure of a downside (tackiness) builds credibility that pure positivity cannot',
        'Specific community identity (dark skin beauty) creates strong viewer connection',
        '"Will you try it?" CTA naturally drives comments and algorithmic amplification',
      ],
      ...post('7648726933840153886', 'https://www.tiktok.com/@dontaskdani/video/7648726933840153886'),
    },
  ],
  creatorGuidePrompts: [
    {
      label: 'East Asian English-speaking female creator',
      prompt:
        'Female creator of East Asian descent, English-speaking, US-based. Age 22–30. Experience creating skincare or sunscreen content on TikTok. 10K–100K followers. Films in natural personal settings — car, bedroom, or vanity — with good natural light. Rapid-fire benefit-listing style, authentic friend-recommendation tone, comfortable using trending background music or audio.',
    },
    {
      label: 'Hispanic female creator (text-overlay format)',
      prompt:
        'Hispanic female creator, US-based. Age 20–28. Experience with TikTok beauty or skincare content. 10K–100K followers. Comfortable producing text-overlay or silent video format — no Spanish voiceover required. Prefers outdoor or GRWM filming contexts, uses trending background music, natural participatory style ("come try this with me").',
    },
    {
      label: 'Dark skin specialist series creator',
      prompt:
        'Female creator (Black or dark skin tone), US-based. Runs a recurring skincare or beauty testing series specifically for dark skin. 5K–50K followers. Known for honestly testing products for white cast and oiliness, including any downsides in the review, with a strong existing community in the dark skin beauty niche.',
    },
  ],
  creatorPersonas: [],
};
